import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import YoutubePlayer from '../../components/youtubePlayer/youtubePlayer';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import { COLORS, SHADOWS } from '../../constants/theme';
import { parseDescriptionToHtml } from './utils';
import RenderHtml from 'react-native-render-html';

const MediaVideoDetails = (props) => {
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const videoId = props?.route?.params?.data;

  useEffect(() => {
    if (videoId) {
      setLoading(true);
      ajax
        .get(Urls.GET_POST + 'VIDEO')
        .then(({ data }) => {
          if (data.status) {
            const list = data?.data?.mediahub || data?.payload || [];
            const found = list.find((item) => String(item.id) === String(videoId));
            if (found) {
              setVideoData(found);
            } else if (list.length > 0) {
              setVideoData(list[0]);
            }
          }
        })
        .catch((error) => console.log('error fetching video details', error))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [videoId]);

  const formattedHtml = parseDescriptionToHtml(videoData?.desc || videoData?.description);

  return (
    <View style={[styles.root, isMobile && styles.rootMobile]}>
      {/* Breadcrumb Header */}
      <View style={styles.breadcrumbBar}>
        <TouchableOpacity
          onPress={() => props.navigation?.navigate('MediaHub')}
          style={styles.backBtn}
          activeOpacity={0.8}
        >
          <Text style={styles.breadText}>Back / Media Hub</Text>
        </TouchableOpacity>
        <Text style={styles.breadSep}>/</Text>
        <Text style={styles.breadActive} numberOfLines={1}>
          {videoData?.title || 'Video Details'}
        </Text>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <View style={styles.loaderWrap}>
            <ActivityIndicator size="large" color="#6C2BD9" />
          </View>
        ) : videoData ? (
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {/* Category Tag */}
            <View style={styles.categoryBadge}>
              <Text style={styles.categoryText}>Video Resource</Text>
            </View>

            {/* Video Title */}
            <Text style={styles.title}>{videoData.title}</Text>

            {/* Video Player */}
            <View style={styles.playerWrap}>
              <YoutubePlayer url={videoData.video_id} height={isMobile ? 220 : 420} width={'100%'} />
            </View>

            {/* Description */}
            <View style={styles.bodyContent}>
              <RenderHtml
                contentWidth={width - 80}
                source={{ html: formattedHtml }}
                tagsStyles={{
                  p: { fontSize: 15, lineHeight: 24, color: '#374151', marginBottom: 14 },
                }}
              />
            </View>

            {/* Bottom Back Button */}
            <TouchableOpacity
              style={styles.bottomBackBtn}
              onPress={() => props.navigation?.navigate('MediaHub')}
              activeOpacity={0.8}
            >
              <Ionicons name="arrow-back" size={16} color="#6C2BD9" />
              <Text style={styles.bottomBackText}>Back to Media Hub</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.loaderWrap}>
            <Text style={{ fontSize: 14, color: '#9CA3AF' }}>Video details not found.</Text>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F5F7FB',
    paddingHorizontal: 40,
    paddingVertical: 20,
  },
  rootMobile: {
    padding: 14,
  },
  breadcrumbBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  breadSep: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  breadActive: {
    fontSize: 12,
    color: '#6C2BD9',
    fontWeight: '600',
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  loaderWrap: {
    paddingVertical: 60,
    alignItems: 'center',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 28,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    ...SHADOWS.sm,
  },
  cardMobile: {
    padding: 16,
    borderRadius: 14,
  },
  categoryBadge: {
    backgroundColor: '#F0E8FF',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  categoryText: {
    color: '#6C2BD9',
    fontSize: 11,
    fontWeight: '700',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
    lineHeight: 32,
    marginBottom: 20,
  },
  playerWrap: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 24,
    backgroundColor: '#000',
  },
  bodyContent: {
    width: '100%',
    marginBottom: 20,
  },
  bottomBackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F0E8FF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    alignSelf: 'flex-start',
    marginTop: 10,
  },
  bottomBackText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6C2BD9',
  },
});

export default drawerHoc({
  Component: MediaVideoDetails,
  showBackButton: true,
  noScroll: true,
});